import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate, statusMeta } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, ChevronRight, HardHat } from "lucide-react";
import { toast } from "sonner";

export default function ProjectReport() {
  const { id } = useParams();
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}/report`).then((r) => setReport(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  const tradesWithPayments = useMemo(
    () => (report?.trades || []).filter((t) => (t.payments || []).length > 0),
    [report?.trades]
  );
  const hasAnyPayments = tradesWithPayments.length > 0;

  if (!report) return <div className="text-slate-500">Loading…</div>;
  const { project, summary: s, trades, generated_at, generated_by } = report;
  const over = (s.over_under_budget || 0) < 0;

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Toolbar (hidden on print) */}
      <div className="print:hidden flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Link to={`/projects/${id}`} className="text-slate-500 hover:text-brandblue inline-flex items-center gap-1" data-testid="link-back-project">
            <ArrowLeft className="h-4 w-4" /> Back to project
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
          <span className="text-slate-700 font-medium">Report</span>
        </div>
        <Button
          onClick={() => window.print()}
          className="h-12 bg-brandblue hover:bg-brandblue-hover text-white px-5"
          data-testid="btn-print-report"
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>

      {/* Report body */}
      <div className="bg-white border border-border rounded-lg shadow-sm p-6 md:p-10 print:border-0 print:shadow-none print:p-0 space-y-8 print:space-y-6" data-testid="report-body">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 pb-6 border-b border-border">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-9 w-9 rounded-md bg-navy flex items-center justify-center">
                <HardHat className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-slate-900">BuildTrack</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Project cost report</div>
              </div>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">{project.name}</h1>
            {project.client_name && <div className="text-sm text-slate-600 mt-1">Client: <span className="font-semibold">{project.client_name}</span></div>}
            {project.address && <div className="text-sm text-slate-500">{project.address}</div>}
          </div>
          <div className="text-xs text-slate-500 text-left md:text-right">
            <div>Generated: <span className="text-slate-700 font-medium">{fmtDate(generated_at)}</span></div>
            <div>Prepared by: <span className="text-slate-700 font-medium">{generated_by.name}</span></div>
            <div>{generated_by.email}</div>
          </div>
        </div>

        {/* Financial summary */}
        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Financial summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ReportStat label="Original estimate" value={fmtMoney(s.project_original_estimate)} />
            <ReportStat label="Current updated est." value={fmtMoney(s.current_updated_estimate)} />
            <ReportStat label="Total quotes" value={fmtMoney(s.total_quotes_value)} />
            <ReportStat label="Approved variations" value={fmtMoney(s.approved_variations)} />
            <ReportStat label="Total paid" value={fmtMoney(s.total_paid)} tone="paid" />
            <ReportStat label="Total owing" value={fmtMoney(s.total_owing)} tone="part" />
            <ReportStat label="Estimated final cost" value={fmtMoney(s.estimated_final_cost)} />
            <ReportStat
              label={over ? "Over budget" : "Under budget"}
              value={fmtMoney(Math.abs(s.over_under_budget || 0))}
              tone={over ? "danger" : "paid"}
            />
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-slate-500 font-medium">Budget used</span>
            <span className="font-display font-bold text-lg" data-testid="report-percent">{s.percent_budget_used || 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-1">
            <div className={`h-full ${over ? "bg-status-danger-text" : "bg-status-paid-text"}`} style={{ width: `${Math.min(s.percent_budget_used || 0, 100)}%` }} />
          </div>
        </section>

        {/* Trades table */}
        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Trades & cost categories</h2>
          {trades.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No trades recorded.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border rounded-md overflow-hidden" data-testid="report-trades-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider">Trade</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider">Est. Final</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider">Paid</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider">Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {trades.map((t) => {
                    const meta = statusMeta(t.status);
                    return (
                      <tr key={t.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2.5">
                          <div className="font-semibold text-slate-900">{t.name}</div>
                          <div className="text-xs text-slate-500">{t.supplier || "—"} {t.category ? `· ${t.category}` : ""}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium">{fmtMoney(t.finance.estimated_final)}</td>
                        <td className="px-3 py-2.5 text-right text-status-paid-text font-semibold">{fmtMoney(t.finance.total_paid)}</td>
                        <td className={`px-3 py-2.5 text-right font-semibold ${t.finance.remaining_balance > 0 ? "text-status-part-text" : "text-status-paid-text"}`}>
                          {fmtMoney(t.finance.remaining_balance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 font-display font-bold">
                  <tr>
                    <td colSpan={2} className="px-3 py-2.5 text-slate-700">Totals</td>
                    <td className="px-3 py-2.5 text-right">{fmtMoney(s.estimated_final_cost)}</td>
                    <td className="px-3 py-2.5 text-right text-status-paid-text">{fmtMoney(s.total_paid)}</td>
                    <td className="px-3 py-2.5 text-right text-status-part-text">{fmtMoney(s.total_owing)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {/* Payments */}
        <section>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Payment history</h2>
          {!hasAnyPayments ? (
            <div className="text-sm text-slate-500 italic">No payments recorded.</div>
          ) : (
            <div className="space-y-6">
              {tradesWithPayments.map((t) => (
                <div key={t.id}>
                  <div className="font-semibold text-slate-800 mb-2">{t.name} <span className="text-slate-500 font-normal">— {t.supplier || "—"}</span></div>
                  <table className="w-full text-sm border border-border rounded-md overflow-hidden">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider">Date</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider">Invoice #</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider">Method</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {t.payments.map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2">{fmtDate(p.payment_date)}</td>
                          <td className="px-3 py-2 text-slate-500">{p.invoice_number || "—"}</td>
                          <td className="px-3 py-2 text-slate-500 uppercase tracking-wider text-xs">{(p.payment_method || "").replace("_", " ")}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(p.amount, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="pt-6 border-t border-border text-[11px] text-slate-400 text-center">
          BuildTrack — cost report generated {fmtDate(generated_at)}. All figures in AUD.
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
}

function ReportStat({ label, value, tone = "neutral" }) {
  const c = {
    neutral: "text-slate-900",
    paid: "text-status-paid-text",
    part: "text-status-part-text",
    danger: "text-status-danger-text",
  }[tone];
  return (
    <div className="border border-border rounded-md p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display font-bold text-lg mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}
